import { createSlice } from '@reduxjs/toolkit';

import thunks from './addressSlice.thunks';
import type {
  AddressDataType,
} from '../../api/api.types';

type InitialStateType = {
  addresses: AddressDataType[];
  suggestions: AddressDataType[];
  activeTable: string;
  loading: boolean;
  suggestionsLoading: boolean;
  suggestionsError: unknown;
  suggestionsRequestId: string | null;
  activeTableRowsCount: number;
  reserveTableRowsCount: number;
};

const getInitialStore = (): InitialStateType => ({
  addresses: [],
  suggestions: [],
  loading: false,
  suggestionsLoading: false,
  suggestionsError: null,
  suggestionsRequestId: null,
  activeTable: '1',
  activeTableRowsCount: 0,
  reserveTableRowsCount: 0,
});

const addressSlice = createSlice({
  name: 'address',
  initialState: getInitialStore,
  reducers: {
    clearSuggestions: (store) => {
      store.suggestions = [];
      store.suggestionsLoading = false;
      store.suggestionsError = null;
      store.suggestionsRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(thunks.getAddressesByPostcode.fulfilled, (store, { payload }) => {
      store.addresses = payload;
    });
    builder.addCase(thunks.getAddressesByPostcode.pending, (store) => {
      store.addresses = [];
    });

    builder.addCase(thunks.getAddressesByText.fulfilled, (store, { payload }) => {
      store.addresses = payload;
    });
    builder.addCase(thunks.getAddressesByText.pending, (store) => {
      store.addresses = [];
    });
    builder.addCase(thunks.getAddressesByTextOrPostcode.fulfilled, (store, { payload }) => {
      store.addresses = payload;
    });
    builder.addCase(thunks.getAddressesByTextOrPostcode.pending, (store) => {
      store.addresses = [];
    });
    builder.addCase(thunks.getAddressSuggestions.pending, (store, { meta }) => {
      store.suggestionsLoading = true;
      store.suggestionsError = null;
      store.suggestionsRequestId = meta.requestId;
    });
    builder.addCase(thunks.getAddressSuggestions.fulfilled, (store, { payload, meta }) => {
      if (store.suggestionsRequestId !== meta.requestId) {
        return;
      }

      store.suggestions = payload.slice(0, 20);
      store.suggestionsLoading = false;
      store.suggestionsRequestId = null;
    });
    builder.addCase(thunks.getAddressSuggestions.rejected, (store, { payload, meta }) => {
      if (store.suggestionsRequestId !== meta.requestId) {
        return;
      }

      store.suggestions = [];
      store.suggestionsLoading = false;
      store.suggestionsError = payload;
      store.suggestionsRequestId = null;
    });
    builder.addCase(thunks.toggleTable.pending, (store) => {
      store.loading = true;
    });
    builder.addCase(thunks.getActiveTableInfo.pending, (store) => {
      store.loading = true;
    });
    builder.addCase(thunks.clearReverseTable.pending, (store) => {
      store.loading = true;
    });
    builder.addCase(thunks.toggleTable.fulfilled, (store, { payload }) => {
      store.activeTable = payload.activeTable;
      store.activeTableRowsCount = payload.activeTableRowsCount;
      store.reserveTableRowsCount = payload.reserveTableRowsCount;
      store.loading = false;
    });
    builder.addCase(thunks.getActiveTableInfo.fulfilled, (store, { payload }) => {
      store.activeTable = payload.activeTable;
      store.activeTableRowsCount = payload.activeTableRowsCount;
      store.reserveTableRowsCount = payload.reserveTableRowsCount;
      store.loading = false;
    });
    builder.addCase(thunks.clearReverseTable.fulfilled, (store) => {
      store.reserveTableRowsCount = 0;
      store.loading = false;
    });
    builder.addCase(thunks.toggleTable.rejected, (store) => {
      store.loading = false;
    });
    builder.addCase(thunks.getActiveTableInfo.rejected, (store) => {
      store.loading = false;
    });
    builder.addCase(thunks.clearReverseTable.rejected, (store) => {
      store.loading = false;
    });
  },
});

export const addressSliceActions = addressSlice.actions;

export default addressSlice.reducer;
